import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowLeft,
  Heart,
  Edit,
  Trash2,
  Play,
  Youtube,
  Music,
  ChevronUp,
  ChevronDown,
  Link as LinkIcon,
  Eye,
  EyeOff,
  RotateCcw,
  Sparkles,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { isSinger, bpmToTempoLabel, stripChords, compatibilityToLabel, isChordLine, chordToSpanish, parseChordPositions } from '../utils/singerFriendly';
import { extractChordSequenceWithMapping } from '../utils/chordSequence';
import { ChordPlayer } from '../components/ChordPlayer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHROMATIC_SCALE = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

export const SongDetailPage = () => {
  const { songId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  const singerMode = isSinger(user);

  const [song, setSong] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [transposedKey, setTransposedKey] = useState('');
  const [transposedLyrics, setTransposedLyrics] = useState('');
  const [showChords, setShowChords] = useState(!singerMode);
  const [compatibleSongs, setCompatibleSongs] = useState([]);
  const [activeChordIndex, setActiveChordIndex] = useState(-1);
  const [chordValidation, setChordValidation] = useState(null); // AI validation result
  const [validating, setValidating] = useState(false);
  const [generatingChords, setGeneratingChords] = useState(false);
  const [generatedChords, setGeneratedChords] = useState(null); // AI generated lyrics with chords
  const [savingChords, setSavingChords] = useState(false);

  // Extract chord sequence from transposed lyrics for the MIDI player
  const { chords: chordSequence, mapping: chordMapping } = useMemo(
    () => extractChordSequenceWithMapping(transposedLyrics),
    [transposedLyrics]
  );

  const handleChordChange = useCallback((index) => {
    setActiveChordIndex(index);
  }, []);

  const handleValidateChords = async () => {
    setValidating(true);
    setChordValidation(null);
    try {
      const response = await fetch(`${API}/ai/validate-chords`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ song_id: songId })
      });
      if (response.ok) {
        const data = await response.json();
        setChordValidation(data.validation);
      } else {
        const err = await response.json().catch(() => ({ detail: 'Error desconocido' }));
        toast.error(err.detail || 'Error al validar acordes');
      }
    } catch (error) {
      toast.error('Error de conexión al validar acordes');
    } finally {
      setValidating(false);
    }
  };

  const handleGenerateChords = async () => {
    setGeneratingChords(true);
    setGeneratedChords(null);
    try {
      const response = await fetch(`${API}/ai/generate-chords`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ song_id: songId })
      });
      if (response.ok) {
        const data = await response.json();
        setGeneratedChords(data.lyrics_with_chords);
        toast.success('Acordes generados exitosamente');
      } else {
        const err = await response.json().catch(() => ({ detail: 'Error desconocido' }));
        toast.error(err.detail || 'Error al generar acordes');
      }
    } catch (error) {
      toast.error('Error de conexión al generar acordes');
    } finally {
      setGeneratingChords(false);
    }
  };

  const handleSaveGeneratedChords = async () => {
    if (!generatedChords || !song) return;
    setSavingChords(true);
    try {
      const response = await fetch(`${API}/songs/${songId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...song,
          lyrics: generatedChords
        })
      });
      if (response.ok) {
        toast.success('Acordes guardados en la canción');
        setGeneratedChords(null);
        // Refresh song data
        const updated = await response.json();
        setSong(updated);
        setTransposedLyrics(updated.lyrics);
        setTransposedKey(updated.original_key);
      } else {
        const err = await response.json().catch(() => ({ detail: 'Error desconocido' }));
        toast.error(err.detail || 'Error al guardar');
      }
    } catch (error) {
      toast.error('Error de conexión al guardar');
    } finally {
      setSavingChords(false);
    }
  };

  useEffect(() => {
    fetchSong();
    fetchCompatibleSongs();
    checkFavorite();
  }, [songId]);

  const fetchSong = async () => {
    try {
      const response = await fetch(`${API}/songs/${songId}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSong(data);
        setTransposedKey(data.original_key);
        setTransposedLyrics(data.lyrics);
      } else {
        toast.error('Canción no encontrada');
        navigate('/songs');
      }
    } catch (error) {
      console.error('Error fetching song:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompatibleSongs = async () => {
    try {
      const response = await fetch(`${API}/songs/compatible/${songId}?limit=5`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCompatibleSongs(data);
      }
    } catch (error) {
      console.error('Error fetching compatible songs:', error);
    }
  };

  const checkFavorite = async () => {
    try {
      const response = await fetch(`${API}/favorites`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setIsFavorite(data.some(s => s.song_id === songId));
      }
    } catch (error) {
      console.error('Error checking favorite:', error);
    }
  };

  const handleToggleFavorite = async () => {
    try {
      const response = await fetch(`${API}/favorites/${songId}`, {
        method: isFavorite ? 'DELETE' : 'POST',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        setIsFavorite(!isFavorite);
        toast.success(isFavorite ? 'Eliminado de favoritos' : 'Agregado a favoritos');
      }
    } catch (error) {
      toast.error('Error al actualizar favoritos');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Estás seguro de eliminar esta canción?')) return;
    
    try {
      const response = await fetch(`${API}/songs/${songId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Canción eliminada');
        navigate('/songs');
      }
    } catch (error) {
      toast.error('Error al eliminar la canción');
    }
  };

  // Render a lyrics line with chords positioned exactly above the correct characters.
  // globalChordStart is the index into the global chord sequence for the first chord in this line.
  const renderLyricsWithChords = (lyricsText, chordLine, globalChordStart = -1) => {
    const chordPositions = parseChordPositions(chordLine);
    if (!chordPositions.length) return lyricsText;

    const sorted = [...chordPositions].sort((a, b) => a.pos - b.pos);
    const segments = [];
    let lastIdx = 0;

    for (let ci = 0; ci < sorted.length; ci++) {
      const { pos, chord } = sorted[ci];
      const clampedPos = Math.min(pos, lyricsText.length);
      if (clampedPos > lastIdx) {
        segments.push({ type: 'text', content: lyricsText.substring(lastIdx, clampedPos) });
      }
      const globalIdx = globalChordStart >= 0 ? globalChordStart + ci : -1;
      segments.push({ type: 'chord', content: chord, globalIdx });
      lastIdx = clampedPos;
    }
    if (lastIdx < lyricsText.length) {
      segments.push({ type: 'text', content: lyricsText.substring(lastIdx) });
    }

    return segments.map((seg, i) => {
      if (seg.type === 'chord') {
        const name = chordToSpanish(seg.content);
        const isActive = seg.globalIdx >= 0 && seg.globalIdx === activeChordIndex;
        return (
          <span key={i} className={`relative inline cursor-help group ${isActive ? 'ring-2 ring-primary bg-primary/10 rounded px-0.5' : ''}`}>
            <span className={`absolute left-0 font-mono font-bold text-[0.7em] whitespace-nowrap ${isActive ? 'text-primary scale-110' : 'text-primary'}`} style={{ bottom: '100%', lineHeight: 1.4, transition: 'transform 0.2s' }}>
              {seg.content}
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-0.5 px-2 py-1 text-xs bg-popover text-popover-foreground border border-border rounded shadow-md opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                {name}
              </span>
            </span>
          </span>
        );
      }
      return <span key={i}>{seg.content}</span>;
    });
  };

  const transposeChord = (chord, semitones) => {
    const chordRegex = /^([A-G][#b]?)(.*)/;
    const match = chord.match(chordRegex);
    if (!match) return chord;
    
    let [, root, suffix] = match;
    
    // Handle flats
    const flatToSharp = { 'Db': 'C#', 'Eb': 'D#', 'Gb': 'F#', 'Ab': 'G#', 'Bb': 'A#' };
    root = flatToSharp[root] || root;
    
    const idx = CHROMATIC_SCALE.indexOf(root);
    if (idx === -1) return chord;
    
    const newIdx = (idx + semitones + 12) % 12;
    return CHROMATIC_SCALE[newIdx] + suffix;
  };

  const handleTranspose = (semitones) => {
    if (!song) return;
    
    // Transpose the key
    const currentKey = transposedKey;
    const isMinor = currentKey.includes('m') && !currentKey.includes('maj');
    let baseKey = currentKey.replace('m', '');
    
    const flatToSharp = { 'Db': 'C#', 'Eb': 'D#', 'Gb': 'F#', 'Ab': 'G#', 'Bb': 'A#' };
    baseKey = flatToSharp[baseKey] || baseKey;
    
    const idx = CHROMATIC_SCALE.indexOf(baseKey);
    if (idx === -1) return;
    
    const newIdx = (idx + semitones + 12) % 12;
    const newKey = CHROMATIC_SCALE[newIdx] + (isMinor ? 'm' : '');
    setTransposedKey(newKey);
    
    // Transpose chords in lyrics — handles both [Chord] inline and chord-above-line formats
    const newLyrics = transposedLyrics.split('\n').map(line => {
      // Transpose [Chord] inline format — only match actual chords, not section labels like [Coro], [Verso]
      let transposed = line.replace(/\[([A-G][#b]?(?:m|maj|min|dim|aug|sus\d?|add\d+|7|9|11|13|6|\/[A-G][#b]?)*)\]/g, (match, chord) => {
        return '[' + transposeChord(chord, semitones) + ']';
      });
      // Transpose chord-above-line format (standalone chord names)
      if (isChordLine(transposed)) {
        transposed = transposed.replace(/([A-G][#b]?(?:m|maj|min|dim|aug|sus\d?|add\d+|7|9|11|13|6|\/[A-G][#b]?)*)/g, (match) => {
          return transposeChord(match, semitones);
        });
      }
      return transposed;
    }).join('\n');
    setTransposedLyrics(newLyrics);
  };

  const getKeyBadgeClass = (key) => {
    const keyMap = {
      'C': 'bg-key-c', 'G': 'bg-key-g', 'D': 'bg-key-d',
      'A': 'bg-key-a', 'E': 'bg-key-e', 'B': 'bg-key-b',
      'F#': 'bg-key-fsharp', 'Db': 'bg-key-db', 'Ab': 'bg-key-ab',
      'Eb': 'bg-key-eb', 'Bb': 'bg-key-bb', 'F': 'bg-key-f'
    };
    const baseKey = key?.replace('m', '').replace('#', '');
    return keyMap[key] || keyMap[baseKey] || 'bg-primary';
  };

  const getCompatibilityColor = (level) => {
    const colors = {
      'perfect': 'text-green-500',
      'excellent': 'text-blue-500',
      'good': 'text-yellow-500',
      'challenging': 'text-red-500'
    };
    return colors[level] || 'text-muted-foreground';
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-10 bg-muted rounded w-32" />
        <div className="h-64 bg-muted rounded-xl" />
      </div>
    );
  }

  if (!song) return null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="song-detail">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl md:text-3xl font-bold">{song.title}</h1>
          <div className="flex items-center gap-2 mt-1 text-muted-foreground">
            {!singerMode && (
              <Badge className={getKeyBadgeClass(song.original_key)} variant="secondary">
                {song.original_key}
              </Badge>
            )}
            {song.theme && <span>• {song.theme}</span>}
            {song.bpm && (
              singerMode
                ? <span>• {bpmToTempoLabel(song.bpm)}</span>
                : <span>• {song.bpm} BPM</span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={handleToggleFavorite} data-testid="favorite-btn">
            <Heart className={`w-5 h-5 ${isFavorite ? 'fill-destructive text-destructive' : ''}`} />
          </Button>
          <Link to={`/singer?song=${songId}`}>
            <Button variant="outline" size="icon" className="text-accent" data-testid="singer-view-btn">
              <Play className="w-5 h-5" />
            </Button>
          </Link>
          {(song.author_id === user?.user_id || user?.role === 'admin') && (
            <Button variant="outline" size="icon" className="text-destructive" onClick={handleDelete} data-testid="delete-btn">
              <Trash2 className="w-5 h-5" />
            </Button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Lyrics */}
        <Card className="lg:col-span-2 border-border/50">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Letra</CardTitle>
            {!singerMode && (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setShowChords(!showChords)}
                  title={showChords ? 'Ocultar acordes' : 'Mostrar acordes'}
                  data-testid="toggle-chords"
                >
                  {showChords ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </Button>
                <span className="text-sm text-muted-foreground">Tono:</span>
                <Badge className={getKeyBadgeClass(transposedKey)}>{transposedKey}</Badge>
                <span className="text-xs text-muted-foreground">({chordToSpanish(transposedKey)})</span>
                <div className="flex gap-1">
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => handleTranspose(-1)} data-testid="transpose-down">
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => handleTranspose(1)} data-testid="transpose-up">
                    <ChevronUp className="w-4 h-4" />
                  </Button>
                  {transposedKey !== song.original_key && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground"
                      onClick={() => { setTransposedKey(song.original_key); setTransposedLyrics(song.lyrics); }}
                      title="Restablecer tono original"
                      data-testid="transpose-reset"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardHeader>
          {/* Chord Player — only for musicians with chords visible */}
          {!singerMode && showChords && chordSequence.length > 0 && (
            <div className="px-6 pb-2">
              <ChordPlayer
                chordSequence={chordSequence}
                defaultBpm={song.bpm || 100}
                onChordChange={handleChordChange}
              />
            </div>
          )}
          <CardContent>
            <div className="font-lyrics text-lg leading-relaxed" data-testid="lyrics-content">
              {(() => {
                const allLines = transposedLyrics.split('\n');
                const elements = [];
                let globalChordCounter = 0; // tracks position in global chord sequence
                for (let i = 0; i < allLines.length; i++) {
                  const line = allLines[i];
                  const isChord = isChordLine(line);

                  if (isChord) {
                    const chordsInLine = parseChordPositions(line).length;
                    const nextLine = (i + 1 < allLines.length && !isChordLine(allLines[i + 1]))
                      ? allLines[i + 1] : '';
                    if (singerMode || !showChords) {
                      if (nextLine) {
                        elements.push(<div key={i} className="whitespace-pre-wrap">{nextLine}</div>);
                        i++;
                      }
                    } else {
                      elements.push(
                        <div key={i} className="whitespace-pre-wrap relative pt-5 mt-1">
                          {renderLyricsWithChords(nextLine, line, globalChordCounter)}
                        </div>
                      );
                      if (nextLine) i++;
                    }
                    globalChordCounter += chordsInLine;
                  } else {
                    const cleaned = singerMode ? line.replace(/\[[^\]]+\]/g, '') : line;
                    elements.push(<div key={i} className="whitespace-pre-wrap">{cleaned || '\u00A0'}</div>);
                  }
                }
                return elements;
              })()}
            </div>
          </CardContent>
        </Card>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Song Info */}
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-lg">Información</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {song.youtube_url && (
                <a
                  href={song.youtube_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-primary hover:underline"
                >
                  <Youtube className="w-4 h-4" />
                  Ver en YouTube
                </a>
              )}
              {song.event && (
                <div className="flex items-center gap-2 text-sm">
                  <Music className="w-4 h-4 text-muted-foreground" />
                  <span>Evento: {song.event}</span>
                </div>
              )}
              {song.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {song.tags.map(tag => (
                    <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* AI Chord Validation — only for musicians */}
          {!singerMode && chordSequence.length > 0 && (
            <Card className="border-border/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  Validar acordes con IA
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  onClick={handleValidateChords}
                  disabled={validating}
                  className="w-full"
                  variant={chordValidation ? 'outline' : 'default'}
                >
                  {validating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analizando...
                    </>
                  ) : chordValidation ? (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Validar de nuevo
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Validar acordes
                    </>
                  )}
                </Button>

                {chordValidation && (
                  <div className="space-y-3 animate-fade-in">
                    {/* Status badge */}
                    <div className="flex items-center gap-2">
                      {chordValidation.is_valid === true ? (
                        <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                          <CheckCircle className="w-3.5 h-3.5 mr-1" />
                          Acordes correctos
                        </Badge>
                      ) : chordValidation.is_valid === false ? (
                        <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                          <XCircle className="w-3.5 h-3.5 mr-1" />
                          Se encontraron problemas
                        </Badge>
                      ) : (
                        <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                          <AlertTriangle className="w-3.5 h-3.5 mr-1" />
                          Resultado parcial
                        </Badge>
                      )}
                      {chordValidation.confidence && (
                        <span className="text-xs text-muted-foreground">
                          Confianza: {chordValidation.confidence}
                        </span>
                      )}
                    </div>

                    {/* Key analysis */}
                    {chordValidation.key_analysis && (
                      <p className="text-sm text-muted-foreground">{chordValidation.key_analysis}</p>
                    )}

                    {/* Issues */}
                    {chordValidation.issues?.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium">Problemas encontrados:</p>
                        {chordValidation.issues.map((issue, i) => (
                          <div key={i} className="text-sm p-2 rounded-lg bg-muted/50 space-y-1">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs font-mono">{issue.current_chord}</Badge>
                              <span className="text-muted-foreground">→</span>
                              <Badge variant="outline" className="text-xs font-mono text-primary">{issue.suggested_chord}</Badge>
                            </div>
                            {issue.location && (
                              <p className="text-xs text-muted-foreground">{issue.location}</p>
                            )}
                            {issue.reason && (
                              <p className="text-xs">{issue.reason}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* General suggestions */}
                    {chordValidation.suggestions && (
                      <p className="text-sm text-muted-foreground border-t border-border/50 pt-2">
                        {chordValidation.suggestions}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* AI Chord Generation — only for musicians when song has NO chords */}
          {!singerMode && chordSequence.length === 0 && song?.lyrics && (
            <Card className="border-border/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  Generar acordes con IA
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Esta canción no tiene acordes. La IA puede generarlos automáticamente.
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  onClick={handleGenerateChords}
                  disabled={generatingChords}
                  className="w-full"
                  variant={generatedChords ? 'outline' : 'default'}
                >
                  {generatingChords ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generando acordes...
                    </>
                  ) : generatedChords ? (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Regenerar acordes
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generar acordes
                    </>
                  )}
                </Button>

                {generatedChords && (
                  <div className="space-y-3 animate-fade-in">
                    <p className="text-sm font-medium">Vista previa de acordes generados:</p>
                    <pre className="text-sm font-mono bg-muted/50 p-3 rounded-lg overflow-x-auto whitespace-pre max-h-64 overflow-y-auto">
                      {generatedChords}
                    </pre>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleSaveGeneratedChords}
                        disabled={savingChords}
                        className="flex-1"
                      >
                        {savingChords ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
                        ) : (
                          <><CheckCircle className="w-4 h-4 mr-2" /> Aplicar acordes</>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setGeneratedChords(null)}
                        disabled={savingChords}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Descartar
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Compatible Songs */}
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <LinkIcon className="w-4 h-4" />
                {singerMode ? 'Canciones similares' : 'Transiciones sugeridas'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {compatibleSongs.length > 0 ? (
                <div className="space-y-3">
                  {compatibleSongs.map((s) => (
                    <Link
                      key={s.song_id}
                      to={`/songs/${s.song_id}`}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors"
                    >
                      {!singerMode && (
                        <div className={`key-badge ${getKeyBadgeClass(s.original_key)} w-8 h-8 text-xs`}>
                          {s.original_key}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{s.title}</p>
                        {singerMode ? (
                          <Badge className={`text-xs ${compatibilityToLabel(s.compatibility?.level).color}`}>
                            {compatibilityToLabel(s.compatibility?.level).text}
                          </Badge>
                        ) : (
                          <p className={`text-xs ${getCompatibilityColor(s.compatibility?.level)}`}>
                            {s.compatibility?.description}
                          </p>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No hay canciones compatibles</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SongDetailPage;
